// src/features/authn/ClientEditDialog.tsx
// OAuth2 client edit — form-kit dialog (docs/ux/14-forms.md) that replaces the
// old routed Card form. Same store wiring: GetOAuth2Client to load,
// UpdateOAuth2Client to save (client_type is immutable after create — the RPC
// has no field for it). The secret is never shown here; rotation lives on the
// detail page.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAuthn, useI18n } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { UriListEditor } from './UriListEditor';
import type { OAuth2Client } from '@core/types/authn';

export interface ClientEditDialogProps {
  open: boolean;
  clientId: string;
  onClose: () => void;
  /** Called after a successful save. */
  onSaved: () => void;
}

const fieldClass =
  'w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring';

export const ClientEditDialog = observer(({ open, clientId, onClose, onSaved }: ClientEditDialogProps) => {
  const authnStore = useAuthn();
  const { t } = useI18n();

  const [clientName, setClientName] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [redirectUris, setRedirectUris] = useState<string[]>([]);
  const [grantTypes, setGrantTypes] = useState('');
  const [scopes, setScopes] = useState('');
  const [responseTypes, setResponseTypes] = useState('');
  const [logoUri, setLogoUri] = useState('');
  const [policyUri, setPolicyUri] = useState('');
  const [tosUri, setTosUri] = useState('');
  const [contacts, setContacts] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  useEffect(() => {
    if (open && clientId) authnStore.GetOAuth2Client(clientId);
  }, [open, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const client = authnStore.currentItem as OAuth2Client | null;
    if (open && client && client.entity?.id === clientId && !loaded) {
      setClientName(client.client_name ?? '');
      setDescription(client.description ?? '');
      setEnabled(client.enabled ?? true);
      setRedirectUris(client.redirect_uris?.items ?? []);
      setGrantTypes((client.allowed_grant_types?.items ?? []).join(', '));
      setScopes((client.allowed_scopes?.items ?? []).join(' '));
      setResponseTypes((client.allowed_response_types?.items ?? []).join(', '));
      setLogoUri(client.logo_uri ?? '');
      setPolicyUri(client.policy_uri ?? '');
      setTosUri(client.tos_uri ?? '');
      setContacts((client.contacts?.items ?? []).join(', '));
      setLoaded(true);
    }
  }, [open, clientId, authnStore.currentItem, loaded]);

  const touch = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setDirty(true);
  };

  const submit = async () => {
    if (!clientName.trim() || !loaded || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await authnStore.UpdateOAuth2Client(clientId, {
        entity: {} as never,
        client_name: clientName,
        description,
        redirect_uris: { items: redirectUris.map((s) => s.trim()).filter(Boolean) },
        allowed_grant_types: { items: grantTypes.split(',').map((s) => s.trim()).filter(Boolean) },
        allowed_scopes: { items: scopes.split(' ').filter(Boolean) },
        allowed_response_types: { items: responseTypes.split(',').map((s) => s.trim()).filter(Boolean) },
        logo_uri: logoUri,
        policy_uri: policyUri,
        tos_uri: tosUri,
        contacts: { items: contacts.split(',').map((s) => s.trim()).filter(Boolean) },
        enabled,
      });
      onSaved();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('common.saveChanges')}
      canSubmit={!!clientName.trim() && loaded}
      isSubmitting={isSubmitting}
      dirty={dirty}
      width="lg"
      aria-label={t('authn.edit')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {!!submitError && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
        {!!authnStore.error && <ErrorAlert error={authnStore.error} dismissible onDismiss={() => authnStore.clearError()} />}

        {!loaded ? (
          <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>
        ) : (
          <>
            <TitleInput
              value={clientName}
              onChange={touch(setClientName)}
              placeholder={t('authn.clientNamePlaceholder')}
              initialFocus
              onEnter={() => document.getElementById('authn-edit-description')?.focus()}
              aria-label={t('authn.clientName')}
            />
            <AutoGrowTextarea
              id="authn-edit-description"
              value={description}
              onChange={touch(setDescription)}
              placeholder={t('authn.descriptionPlaceholder')}
              minRows={1}
            />

            <FormRow label={t('authn.enabled')} labelWidth="sm">
              <ToggleSwitch label={t('authn.enabled')} checked={enabled} onChange={touch(setEnabled)} />
            </FormRow>

            <FormRow label={t('authn.redirectUris')} hint={t('authn.redirectUrisHelper')} labelWidth="sm">
              <UriListEditor
                value={redirectUris}
                onChange={touch(setRedirectUris)}
                addLabel={t('authn.addRedirectUri')}
                emptyLabel={t('authn.noRedirectUris')}
                placeholder={t('authn.redirectUriPlaceholder')}
                removeLabel={t('common.remove')}
                idPrefix="authn-edit-uri"
              />
            </FormRow>

            <div className="border-t border-border pt-1">
              <FormRow label={t('authn.grantTypes')} labelWidth="sm" htmlFor="authn-edit-grants">
                <input id="authn-edit-grants" type="text" value={grantTypes} onChange={(e) => touch(setGrantTypes)(e.target.value)} autoCapitalize="off" className={fieldClass} />
              </FormRow>
              <FormRow label={t('authn.scopes')} labelWidth="sm" htmlFor="authn-edit-scopes">
                <input id="authn-edit-scopes" type="text" value={scopes} onChange={(e) => touch(setScopes)(e.target.value)} autoCapitalize="off" className={fieldClass} />
              </FormRow>
              <FormRow label={t('authn.responseTypes')} labelWidth="sm" htmlFor="authn-edit-responses">
                <input id="authn-edit-responses" type="text" value={responseTypes} onChange={(e) => touch(setResponseTypes)(e.target.value)} autoCapitalize="off" className={fieldClass} />
              </FormRow>
              <FormRow label={t('authn.logoUri')} labelWidth="sm" htmlFor="authn-edit-logo">
                <input id="authn-edit-logo" type="text" value={logoUri} onChange={(e) => touch(setLogoUri)(e.target.value)} autoCapitalize="off" className={`${fieldClass} font-mono`} />
              </FormRow>
              <FormRow label={t('authn.policyUri')} labelWidth="sm" htmlFor="authn-edit-policy">
                <input id="authn-edit-policy" type="text" value={policyUri} onChange={(e) => touch(setPolicyUri)(e.target.value)} autoCapitalize="off" className={`${fieldClass} font-mono`} />
              </FormRow>
              <FormRow label={t('authn.tosUri')} labelWidth="sm" htmlFor="authn-edit-tos">
                <input id="authn-edit-tos" type="text" value={tosUri} onChange={(e) => touch(setTosUri)(e.target.value)} autoCapitalize="off" className={`${fieldClass} font-mono`} />
              </FormRow>
              <FormRow label={t('authn.contacts')} hint={t('authn.contactsHelper')} labelWidth="sm" htmlFor="authn-edit-contacts">
                <input id="authn-edit-contacts" type="text" value={contacts} onChange={(e) => touch(setContacts)(e.target.value)} autoCapitalize="off" className={fieldClass} />
              </FormRow>
            </div>
          </>
        )}
      </div>
    </FormDialog>
  );
});

export default ClientEditDialog;
